import React, { useState, useRef, useEffect } from 'react';
import { IconBell } from './Icons';
import './NotificationBell.css';

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const TYPE_ICONS = {
  mention: '@',
  reply: '\u21A9',
  decision: '\u2713',
  todo: '\u2610',
};

function NotificationBell({
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onNavigate,
  // Global mode: the cross-workspace bell next to the username. Shows each
  // notification's workspace in the meta line and hands the whole notification
  // (not just a room id) to onMarkRead/onNavigate so the app can switch
  // workspaces before opening the thread.
  global = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleClick = (notif) => {
    if (!notif.read) onMarkRead(global ? notif : notif.id);
    onNavigate(global ? notif : notif.room_id || notif.roomId);
    setOpen(false);
  };

  return (
    <div className="notif-bell-wrapper" ref={ref}>
      <button
        className="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <IconBell size={14} className="notif-bell-icon" />
        {unreadCount > 0 && (
          <span className="notif-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span className="notif-dropdown-title">
              {global ? 'All notifications' : 'Notifications'}
            </span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="notif-empty">No notifications yet</div>
          ) : (
            <ul className="notif-list">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`notif-item ${n.read ? '' : 'unread'}`}
                  onClick={() => handleClick(n)}
                >
                  <span className={`notif-type-icon notif-type-${n.type}`}>
                    {TYPE_ICONS[n.type] || '\u2022'}
                  </span>
                  <div className="notif-content">
                    <span className="notif-title">{n.title}</span>
                    {n.body && <span className="notif-body">{n.body}</span>}
                    <span className="notif-meta">
                      {[
                        global ? n.workspace_name || n.workspaceName : null,
                        n.room_name || n.roomName,
                        timeAgo(n.created_at || n.createdAt),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  {!n.read && <span className="notif-unread-dot" />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
